import { useAccount, useReadContracts } from 'wagmi'
import { T } from '@/lib/theme'
import { SectionHeader } from '@/components/dashboard/SectionHeader'
import { HeroFigure } from '@/components/dashboard/HeroFigure'
import { TokenGlyph } from '@/components/dashboard/TokenGlyph'
import { Button, Panel, SectionLabel, Stat, Row } from '@/components/ui'
import { LiveCountdown } from '@/components/LiveCountdown'
import { ADDRESSES, IS_LIVE, ZERO_ADDR } from '@/lib/contracts'
import { ERC20_ABI } from '@/lib/erc20'
import buybackArrayAbi from '@/lib/abis/HorizonBuybackArray.json'
import auctionAbi from '@/lib/abis/HorizonAuction.json'
import oracleAbi from '@/lib/abis/HorizonUsdOracle.json'
import { fmt, fmtUsd, toNumber, POLL_FAST } from '@/lib/utils'
import { useContractTx } from '@/hooks/useContractTx'

const E18 = 10n ** 18n
const TEAL = '#2DD4BF' // X28 accent (no teal token in the theme; TITANX uses T.positive = TitanX green)

// The buyback array's address book key (falls back to BUYBACK, which now points at the array).
const ARRAY_ADDR = ADDRESSES.HORIZON_BUYBACK_ARRAY || ADDRESSES.BUYBACK
const ARRAY_NOT_DEPLOYED = !IS_LIVE || (ARRAY_ADDR || '').toLowerCase() === ZERO_ADDR.toLowerCase()

// ─────────────────────────────────────────────────────────────────
// The TitanX-aligned buy-and-burn engine, presented as its three cascading lanes (the AURYN /
// TitanX Buy & Burn shape). Value flows one hop per lane: ETH buys TITANX, TITANX becomes X28
// (minted 1:1 at peg, bought on-market below it), X28 buys HORIZON that is burned or banked to
// treasury. Each lane is permissionless: anyone can process an open lane and earn its incentive.
// ─────────────────────────────────────────────────────────────────

export function BuybackSection() {
  return (
    <div id="buyback">
      <SectionHeader
        title="Buyback"
        subtitle="A share of every protocol fee cascades through TITANX and X28 into HORIZON. A standing bid that grows with usage."
      />
      {ARRAY_NOT_DEPLOYED ? <BuybackPlaceholder /> : <BuybackArray />}
    </div>
  )
}

function BuybackArray() {
  const { address: user } = useAccount()

  const { data: reads, refetch } = useReadContracts({
    query: { enabled: !ARRAY_NOT_DEPLOYED, refetchInterval: POLL_FAST },
    contracts: [
      { address: ARRAY_ADDR, abi: buybackArrayAbi, functionName: 'pending' },            // 0 [eth,titanx,x28]
      { address: ARRAY_ADDR, abi: buybackArrayAbi, functionName: 'ethLane' },            // 1
      { address: ARRAY_ADDR, abi: buybackArrayAbi, functionName: 'titanxLane' },         // 2
      { address: ARRAY_ADDR, abi: buybackArrayAbi, functionName: 'x28Lane' },            // 3
      { address: ARRAY_ADDR, abi: buybackArrayAbi, functionName: 'burnBps' },            // 4
      { address: ARRAY_ADDR, abi: buybackArrayAbi, functionName: 'horizonX28Pool' },     // 5
      { address: ARRAY_ADDR, abi: buybackArrayAbi, functionName: '_mintFavorable' },     // 6
      { address: ADDRESSES.WETH,   abi: ERC20_ABI, functionName: 'balanceOf', args: [ARRAY_ADDR] }, // 7
      { address: ADDRESSES.TITANX, abi: ERC20_ABI, functionName: 'balanceOf', args: [ARRAY_ADDR] }, // 8
      { address: ADDRESSES.X28,    abi: ERC20_ABI, functionName: 'balanceOf', args: [ARRAY_ADDR] }, // 9
      { address: ADDRESSES.HORIZON_AUCTION, abi: auctionAbi, functionName: 'oracle' },               // 10
      { address: ARRAY_ADDR, abi: buybackArrayAbi, functionName: '_titanxPerX28' },                 // 11
      { address: ARRAY_ADDR, abi: buybackArrayAbi, functionName: 'dailyBps' },                       // 12
    ],
  })

  const pending   = reads?.[0]?.result ?? [0n, 0n, 0n]
  const ethLane   = reads?.[1]?.result ?? [0n, 0n, 0n, 0n]
  const titanxLane = reads?.[2]?.result ?? [0n, 0n, 0n, 0n]
  const x28Lane   = reads?.[3]?.result ?? [0n, 0n, 0n, 0n]
  const burnBps   = Number(reads?.[4]?.result ?? 0n)
  const horizonX28Pool = reads?.[5]?.result ?? ZERO_ADDR
  const mintFavorable = reads?.[6]?.result ?? false
  const wethHeld   = reads?.[7]?.result ?? 0n
  const titanxHeld = reads?.[8]?.result ?? 0n
  const x28Held    = reads?.[9]?.result ?? 0n

  const x28PoolSet = (horizonX28Pool || ZERO_ADDR).toLowerCase() !== ZERO_ADDR.toLowerCase()

  // USD values via the minter's price oracle (wired at launch). WETH + TITANX price directly; X28 is
  // priced through its TITANX-equivalent (x28 balance × the spot TITANX-per-X28 the array exposes).
  const oracleAddr = reads?.[10]?.result ?? ZERO_ADDR
  const titanxPerX28 = reads?.[11]?.result ?? 0n
  const dailyBps = Number(reads?.[12]?.result ?? 0n) // the daily buyback rate (e.g. 2800 = 28%/day)
  const oracleSet = (oracleAddr || ZERO_ADDR).toLowerCase() !== ZERO_ADDR.toLowerCase()
  const x28InTitanx = titanxPerX28 > 0n ? (x28Held * titanxPerX28) / E18 : 0n

  const { data: usdReads } = useReadContracts({
    query: { enabled: !ARRAY_NOT_DEPLOYED && oracleSet, refetchInterval: POLL_FAST },
    contracts: [
      { address: oracleAddr, abi: oracleAbi, functionName: 'valueInUsd', args: [ADDRESSES.WETH, wethHeld] },
      { address: oracleAddr, abi: oracleAbi, functionName: 'valueInUsd', args: [ADDRESSES.TITANX, titanxHeld] },
      { address: oracleAddr, abi: oracleAbi, functionName: 'valueInUsd', args: [ADDRESSES.TITANX, x28InTitanx] },
    ],
  })
  const ethUsd    = usdReads?.[0]?.result ?? 0n
  const titanxUsd = usdReads?.[1]?.result ?? 0n
  const x28Usd    = usdReads?.[2]?.result ?? 0n
  const totalUsd  = ethUsd + titanxUsd + x28Usd

  const lanes = [
    {
      key: 'eth', fn: 'processEth', accent: T.accent,
      title: 'ETH', flow: 'WETH buys TITANX', asset: 'WETH',
      held: wethHeld, usd: oracleSet ? ethUsd : null, pending: pending[0], cfg: ethLane, dormant: false, note: null,
    },
    {
      key: 'titanx', fn: 'processTitanx', accent: T.positive,
      title: 'TITANX', flow: mintFavorable ? 'TITANX mints X28 at peg' : 'TITANX buys X28 on market', asset: 'TITANX',
      held: titanxHeld, usd: oracleSet ? titanxUsd : null, pending: pending[1], cfg: titanxLane, dormant: false,
      note: mintFavorable ? 'At peg · minting X28 one for one' : 'Below peg · buying X28 on market',
    },
    {
      key: 'x28', fn: 'processX28', accent: TEAL,
      title: 'X28', flow: 'X28 buys HORIZON', asset: 'X28',
      held: x28Held, usd: oracleSet ? x28Usd : null, pending: x28PoolSet ? pending[2] : 0n, cfg: x28Lane, dormant: !x28PoolSet,
      note: x28PoolSet
        ? `Split ${burnBps / 100}% burned · ${(100 - burnBps / 100).toFixed(burnBps % 100 ? 2 : 0)}% to treasury`
        : 'Opens once the HORIZON/X28 pool is live',
    },
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
      <HeroFigure
        eyebrow="Total value"
        value={oracleSet ? fmtUsd(toNumber(totalUsd)) : '—'}
      />

      <div style={cascadeRow}>
        {['ETH', 'TITANX', 'X28', 'HORIZON'].map((step, i) => (
          <div key={step} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontFamily: T.fontN, fontWeight: 700, color: i === 3 ? T.positive : T.text, fontSize: T.fs.ui }}>{step}</span>
            {i < 3 && <span style={{ color: T.textDim }}>→</span>}
          </div>
        ))}
      </div>

      <div style={laneGrid}>
        {lanes.map((lane) => (
          <LaneCard key={lane.key} lane={lane} user={user} onTx={refetch} dailyBps={dailyBps} />
        ))}
      </div>

      <MevBlockerNote />
    </div>
  )
}

function LaneCard({ lane, user, onTx, dailyBps }) {
  const tx = useContractTx()
  const [, cooldown, lastCallTs, incentiveBps] = lane.cfg
  const now = Math.floor(Date.now() / 1000)
  const nextCallableAt = Number(lastCallTs) + Number(cooldown)
  const cooldownActive = Number(lastCallTs) > 0 && now < nextCallableAt
  const drawable = toNumber(lane.pending)                              // SHOGUN "Next Buy & Burn" — the next call's slice
  const reward = drawable * (Number(incentiveBps) / 10_000)            // SHOGUN "User Reward" — the caller's bounty on it
  const allocatedToday = toNumber(lane.held) * (dailyBps / 10_000)     // SHOGUN "Allocated Today" — dailyBps% of the balance

  const open = !lane.dormant && !cooldownActive && lane.pending > 0n
  const disabled = !user || !open || tx.pending

  async function trigger() {
    await tx.run(
      { address: ARRAY_ADDR, abi: buybackArrayAbi, functionName: lane.fn },
      { successMsg: `${lane.title} buyback processed.` },
    )
    onTx?.()?.catch?.((e) => console.error('refetch after process failed:', e))
  }

  let label
  if (!user) label = 'Connect wallet'
  else if (tx.pending) label = 'Working…'
  else if (lane.dormant) label = 'Awaiting pool'
  else if (cooldownActive) label = 'Cooling down'
  else if (lane.pending === 0n) label = 'Accruing'
  else label = `Process · earn ${fmt(reward, 4)} ${lane.asset}`

  return (
    <Panel style={{ padding: 26, opacity: lane.dormant ? 0.7 : 1 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <TokenGlyph symbol={lane.asset} size={40} />
            <SectionLabel style={{ color: lane.accent }}>{lane.title}</SectionLabel>
          </div>
          <span style={{ fontFamily: T.fontM, fontSize: T.fs.caption, color: T.textDim }}>{lane.flow}</span>
        </div>

        <Stat label="Balance" value={fmt(toNumber(lane.held), 2)} sub={lane.usd != null ? `${lane.asset} · ${fmtUsd(toNumber(lane.usd))}` : lane.asset} />
        <div>
          <Row label="Allocated today" value={`${fmt(allocatedToday, 2)} ${lane.asset}`} />
          <Row label="Next buyback" value={`${fmt(drawable, 2)} ${lane.asset}`} />
          <Row label="User reward" value={`${fmt(reward, 4)} ${lane.asset}`} />
        </div>

        <div style={{ minHeight: 20, color: lane.dormant ? T.textDim : cooldownActive ? T.textMuted : T.positive, fontFamily: T.fontM, fontSize: T.fs.caption, fontWeight: cooldownActive || lane.dormant ? 400 : 700 }}>
          {lane.dormant
            ? lane.note
            : cooldownActive
              ? <>Opens in <LiveCountdown endSec={nextCallableAt} style={{ color: T.accent, fontFamily: T.fontN, fontWeight: 700 }} /></>
              : lane.note || 'Open for the next call'}
        </div>

        <Button onClick={trigger} disabled={disabled} style={{ width: '100%', padding: '13px 16px', fontSize: T.fs.ui }}>
          {label}
        </Button>
      </div>
    </Panel>
  )
}

function MevBlockerNote() {
  return (
    <Panel style={{ padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div style={{
          flexShrink: 0, width: 40, height: 40, borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: T.positiveDim, color: T.positive,
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            <path d="M9 12l2 2 4-4" />
          </svg>
        </div>
        <div style={{ flex: '1 1 320px', minWidth: 0 }}>
          <div style={{ color: T.text, fontFamily: T.fontD, fontWeight: 700, fontSize: T.fs.ui }}>Shield your buyback from MEV</div>
          <div style={{ color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption, lineHeight: 1.6, marginTop: 3 }}>
            Process a buyback through the MEV Blocker RPC and the swap is protected from sandwich attacks, so
            more value stays in the buyback. One click, once, and it stays on.
          </div>
        </div>
        <Button variant="ghost" onClick={() => window.open('https://mevblocker.io/#rpc', '_blank', 'noreferrer')} style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
          Add MEV Blocker RPC
        </Button>
      </div>
    </Panel>
  )
}

function BuybackPlaceholder() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
      <HeroFigure eyebrow="Value flowing to buyback" value="—" />
      <Panel style={{ padding: 32, textAlign: 'center' }}>
        <div style={{ color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.body, lineHeight: 1.6 }}>
          Live once the protocol starts earning fees.
        </div>
      </Panel>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────────

const cascadeRow = {
  display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: 12,
  padding: '16px 20px',
}
const laneGrid = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20,
}
